import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface PageLoadingProps {
  message?: string;
  className?: string;
}

/**
 * PageLoading Component
 * Full-page loading state with orbital spinner and message
 * Used for page-level data fetching
 */
export const PageLoading = ({
  message = "Loading...",
  className,
}: PageLoadingProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center min-h-[400px] py-12",
        className
      )}
      role="status"
      aria-live="polite"
    >
      {/* Orbital Spinner Container */}
      <div className="relative w-16 h-16 mb-6">
        {/* Outer rotating ring */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-primary/30"
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Inner rotating ring (opposite direction) */}
        <motion.div
          className="absolute inset-2 rounded-full border-2 border-accent/40"
          animate={{ rotate: -360 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Center dot with pulse */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gradient-to-br from-primary to-accent"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.8, 1, 0.8],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Orbital particles */}
        {[0, 120, 240].map((angle, i) => (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2"
            animate={{
              x: [
                Math.cos((angle * Math.PI) / 180) * 30,
                Math.cos(((angle + 360) * Math.PI) / 180) * 30,
              ],
              y: [
                Math.sin((angle * Math.PI) / 180) * 30,
                Math.sin(((angle + 360) * Math.PI) / 180) * 30,
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
              delay: i * 0.2,
            }}
          >
            <div
              className="w-full h-full rounded-full"
              style={{
                background: `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))`,
                boxShadow: "0 0 8px hsl(var(--primary) / 0.5)",
              }}
            />
          </motion.div>
        ))}
      </div>

      {/* Loading Message */}
      <motion.p
        className="text-muted-foreground text-base font-medium"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {message}
      </motion.p>

      {/* Screen reader text */}
      <span className="sr-only">Loading content, please wait</span>
    </div>
  );
};
